import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import {
  getBasicAccount,
  setBasicAccount,
} from '../../store/actions/user/userActions';
import { FormContainer, FormWrap } from '../FormContainer/FormContainer';

import {
  APPLICANT_TYPES,
  APPLICATION_TYPE,
  BASIC_ACCOUNT_UPGRADE_TYPES,
  suffixOptions,
  suffixOptionsValues,
} from '../../util/constants';
import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import useStepPost from '../../hooks/useStepPost';
import usePersonNameDispatch from '../../hooks/usePersonNameDispatch';
import InputText from '../Fields/InputText';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import Select from '../Fields/Select';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  BASIC_ACCOUNT_QUERY_DATA,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  ACCOUNT_TYPE_GTM,
} from '../../constants/sessionStorage';

import useApplicationUid from '../../hooks/useApplicationUid';
import { selectBasicAccount } from '../../store/selectors/userSelectors';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import { getValidationSchemaImpersonate } from '../../util/helpers/getValidationSchemaImpersonate';
import {
  APPLICATION_TYPE_IRA,
  APPLICATION_TYPE_UTMA,
} from '../../constants/accountTypes';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';

import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';

const formValidationSchema = Yup.object({
  ...getValidationSchema({
    suffixOptions,
    fields: ['FirstName', 'MiddleName', 'LastName', 'Suffix'],
  }),
});

const formValidationSchemaImpersonate = Yup.object({
  ...getValidationSchemaImpersonate({
    suffixOptions,
    fields: ['FirstName', 'MiddleName', 'LastName', 'Suffix'],
  }),
});

const AccountNameForm = ({
  nextPath,
  applicantType,
  onModalClose,
  formId,
  setEventType,
  eventType,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const { personNameDispatch } = usePersonNameDispatch();
  const { ApplicationUid } = useApplicationUid();
  const Application = useSelector(selectApplication);
  const { AccountUpgradeType } = Application;
  const isUpgrade =
    AccountUpgradeType === BASIC_ACCOUNT_UPGRADE_TYPES.IN_PROCESS;
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const basicAccountQueryData = retrieveFromSessionStorage(
    BASIC_ACCOUNT_QUERY_DATA,
  );
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    if (applicantType === APPLICANT_TYPES.MAIN) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Secure Account Name',
        accountType: accountGtmType,
      });
      return;
    }
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Secure Account Name Second Owner',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    if (applicantType === APPLICANT_TYPES.MAIN) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_COMPLETE,
        stepLabel: 'Secure Account Name',
        accountType: accountGtmType,
      });
      return;
    }

    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Secure Account Name Second Owner',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
    });
  };

  useEffect(() => {
    const { ApplicationType } = Application;
    if (
      basicAccountQueryData?.accountUid &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_UTMA &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_IRA
    ) {
      dispatch(
        getBasicAccount({
          applicationUid: ApplicationUid,
          accountUid: basicAccountQueryData?.accountUid,
        }),
      );
      return;
    }
    if (
      isUpgrade &&
      Application?.AccountUid &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_UTMA &&
      APPLICATION_TYPE[ApplicationType] !== APPLICATION_TYPE_IRA
    ) {
      dispatch(
        getBasicAccount({
          applicationUid: ApplicationUid,
          accountUid: Application?.AccountUid,
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, Application]);

  const basicAccountData = useSelector(selectBasicAccount);

  useEffect(() => {
    const { ApplicationType } = Application;
    if (
      APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE_UTMA ||
      APPLICATION_TYPE[ApplicationType] === APPLICATION_TYPE_IRA
    ) {
      dispatch(setBasicAccount([]));
    }
  }, [dispatch, Application]);

  const firstName =
    applicantType === APPLICANT_TYPES.MAIN && basicAccountData?.FirstName
      ? basicAccountData?.FirstName
      : '';
  const middleName =
    applicantType === APPLICANT_TYPES.MAIN && basicAccountData?.MiddleName
      ? basicAccountData?.MiddleName
      : '';
  const lastName =
    applicantType === APPLICANT_TYPES.MAIN && basicAccountData?.LastName
      ? basicAccountData?.LastName
      : '';
  const suffix =
    applicantType === APPLICANT_TYPES.MAIN && basicAccountData?.Suffix
      ? {
          label: suffixOptionsValues[basicAccountData?.Suffix],
          value: suffixOptionsValues[basicAccountData?.Suffix],
        }
      : {
          label: 'None',
          value: 'None',
        };

  const ApplicantData = useApplicantData(applicantType);
  const defaultValues = {
    FirstName: getApplicantData(ApplicantData, 'PersonName', 'FirstName')
      ?.length
      ? getApplicantData(ApplicantData, 'PersonName', 'FirstName')
      : firstName,
    MiddleName: getApplicantData(ApplicantData, 'PersonName', 'MiddleName')
      ?.length
      ? getApplicantData(ApplicantData, 'PersonName', 'MiddleName')
      : middleName,
    LastName: getApplicantData(ApplicantData, 'PersonName', 'LastName')?.length
      ? getApplicantData(ApplicantData, 'PersonName', 'LastName')
      : lastName,
    Suffix:
      getApplicantData(ApplicantData, 'PersonName', 'Suffix')?.value !== 'None'
        ? getApplicantData(ApplicantData, 'PersonName', 'Suffix')
        : suffix,
  };

  const { handleSubmit, control, reset } = useForm({
    resolver: yupResolver(
      isSalesAssisted ? formValidationSchemaImpersonate : formValidationSchema,
    ),
    defaultValues: {
      ...defaultValues,
    },
  });

  useEffect(() => {
    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicAccountData]);

  const onSubmit = ({ FirstName, MiddleName, LastName, Suffix }) => {
    if (isSalesAssisted) {
      personNameDispatch({
        data: {
          FirstName,
          MiddleName,
          LastName,
          Suffix,
        },
        personUid: ApplicantData?.PersonUid,
        isPrimary:
          applicantType === APPLICANT_TYPES.MAIN ||
          applicantType === APPLICANT_TYPES.BUSINESS,
        onSuccess:
          eventType === 'Form'
            ? onSuccess
            : () => {
                setHasError(false);
                setSaveAndCloseModalOpen(true);
              },
        onError: () => {
          setHasError(true);
          setSaveAndCloseModalOpen(true);
        },
        defaultValues,
      });
      return;
    }
    personNameDispatch({
      data: {
        FirstName,
        MiddleName,
        LastName,
        Suffix,
      },
      personUid: ApplicantData?.PersonUid,
      isPrimary:
        applicantType === APPLICANT_TYPES.MAIN ||
        applicantType === APPLICANT_TYPES.BUSINESS,
      onSuccess:
        eventType === 'Form' ? onSuccess : () => setSaveAndCloseModalOpen(true),
      onError: () => {
        setHasError(true);
        setSaveAndCloseModalOpen(true);
      },
      defaultValues,
    });
    handleGtmEvent();
  };
  return (
    <FormWrap maxWidth={344} id={formId} onSubmit={handleSubmit(onSubmit)}>
      <FormContainer>
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              label={t('fields.labels.firstName')}
              {...field}
              {...fieldState}
              autocomplete="given-name"
              autoFocus
            />
          )}
          name="FirstName"
        />
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              label={t('fields.labels.middleName')}
              {...field}
              {...fieldState}
              autocomplete="additional-name"
            />
          )}
          name="MiddleName"
        />
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              label={t('fields.labels.lastName')}
              {...field}
              {...fieldState}
              autocomplete="family-name"
            />
          )}
          name="LastName"
        />
        <Controller
          control={control}
          render={({ field: { ref, ...field }, fieldState }) => (
            <Select
              label={t('fields.labels.suffix')}
              options={suffixOptions.map((suffix) => ({
                value: suffix,
                label: suffix,
              }))}
              {...field}
              {...fieldState}
            />
          )}
          name="Suffix"
        />
      </FormContainer>
      <OnBoardingCta
        type="submit"
        onClick={() => setEventType('Form')}
        title={nextPath ? t('continue') : t('update')}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          hasError={hasError}
          onSuccess={onSuccess}
        />
      )}
    </FormWrap>
  );
};

AccountNameForm.propTypes = {
  nextPath: PropTypes.string,
  path: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  formId: PropTypes.string,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
};

export default AccountNameForm;
